import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ModalityBadge, modColor } from "@/components/Shared";
import {
  format, parseISO, subDays,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, addWeeks, subWeeks,
  isSameMonth, isSameDay,
} from "date-fns";
import {
  CalendarClock, Sun, Moon, Clock3, ChevronLeft, ChevronRight,
  CalendarDays, CalendarRange, List as ListIcon,
} from "lucide-react";
import { toast } from "sonner";

const dstr = (d) => format(d, "yyyy-MM-dd");
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function TechHome() {
  const { user, setUser } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(dstr(new Date()));
  const today = new Date();

  useEffect(() => {
    const start = dstr(subDays(today, 40));
    api.get(`/shifts?start=${start}&end=2100-01-01`)
      .then((r) => setShifts(r.data))
      .catch(() => {});
  }, []);

  const savePref = async (p) => {
    try {
      await api.patch("/me/preference", { shift_preference: p });
      setUser({ ...user, shift_preference: p });
      toast.success(`Preference set to ${p === "any" ? "no preference" : p}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const byDate = useMemo(() => {
    const m = {};
    shifts.forEach((s) => { (m[s.date] = m[s.date] || []).push(s); });
    Object.values(m).forEach((list) => list.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return m;
  }, [shifts]);

  const upcoming = useMemo(
    () => shifts.filter((s) => s.date >= dstr(today)).sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time)),
    [shifts]
  );
  const nextShift = upcoming[0];
  const selectedShifts = byDate[selected] || [];

  const monthDays = useMemo(() => {
    const first = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const last = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const days = [];
    for (let d = first; d <= last; d = addDays(d, 1)) days.push(d);
    return days;
  }, [cursor]);

  const weekDays = useMemo(() => {
    const first = startOfWeek(cursor, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(first, i));
  }, [cursor]);

  const stepBack = () => setCursor(view === "week" ? subWeeks(cursor, 1) : subMonths(cursor, 1));
  const stepFwd = () => setCursor(view === "week" ? addWeeks(cursor, 1) : addMonths(cursor, 1));
  const rangeLabel = view === "week"
    ? `${format(weekDays[0], "MMM d")} – ${format(weekDays[6], "MMM d, yyyy")}`
    : format(cursor, "MMMM yyyy");

  const VIEWS = [["month", "Month", CalendarDays], ["week", "Week", CalendarRange], ["list", "List", ListIcon]];

  return (
    <div>
      <PageHeader title={`Hi, ${user?.name}`} subtitle={`// ${user?.category} Technologist — your work schedule`} />

      <div className="bg-[#0F1219] border border-slate-800 p-4 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="pref-card">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] font-mono-rs mb-1">Preferred Working Hours</div>
          <p className="text-xs text-slate-500 max-w-md">
            We&apos;ll try to honor this when building future schedules{user?.category !== "Both" ? "; night shifts require dual (CT + X-ray) certification." : "."}
          </p>
        </div>
        <div className="flex gap-2">
          {[["day", "Days", Sun], ["night", "Nights", Moon], ["any", "No Pref", Clock3]].map(([p, label, Icon]) => (
            <button key={p} data-testid={`pref-btn-${p}`} onClick={() => savePref(p)}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-mono-rs uppercase border transition-all duration-300 ${
                (user?.shift_preference || "any") === p
                  ? "border-[#B15CFF] bg-[#8A2BE2]/20 text-white rs-glow-magenta"
                  : "border-slate-700 text-slate-400 hover:text-white hover:border-slate-500"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* View toggle + range nav */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex border border-slate-800" data-testid="tech-view-toggle">
          {VIEWS.map(([v, label, Icon]) => (
            <button key={v} data-testid={`tech-view-${v}`} onClick={() => setView(v)}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-mono-rs uppercase transition-all duration-300 ${
                view === v ? "bg-[#D902EE]/20 text-white border-b-2 border-[#D902EE]" : "text-slate-400 hover:text-white"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        {view !== "list" && (
          <div className="flex items-center gap-3">
            <button data-testid="cal-prev" onClick={stepBack} className="p-2 border border-slate-800 text-[#00F0FF] hover:border-[#00F0FF]/50 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <span className="font-mono-rs text-sm text-slate-300 min-w-[180px] text-center">{rangeLabel}</span>
            <button data-testid="cal-next" onClick={stepFwd} className="p-2 border border-slate-800 text-[#00F0FF] hover:border-[#00F0FF]/50 transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {/* MONTH */}
      {view === "month" && (
        <div data-testid="tech-month-grid" className="rs-fade-up">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {WEEKDAYS.map((d) => (
              <div key={d} className="text-center text-[10px] tracking-[0.2em] uppercase text-slate-500 font-mono-rs py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {monthDays.map((d) => {
              const key = dstr(d);
              const dayShifts = byDate[key] || [];
              const inMonth = isSameMonth(d, cursor);
              const isToday = isSameDay(d, today);
              const isSel = key === selected;
              return (
                <button key={key} data-testid={`tech-day-${key}`} onClick={() => setSelected(key)}
                  className={`min-h-[84px] p-2 text-left border transition-all duration-200 ${
                    isSel ? "border-[#D902EE] bg-[#D902EE]/10 rs-glow-magenta"
                    : dayShifts.length ? "border-slate-700 bg-[#0F1219] hover:border-[#00F0FF]/50"
                    : "border-slate-800/60 bg-[#0B0D13]"} ${inMonth ? "" : "opacity-40"}`}>
                  <div className={`font-mono-rs text-sm mb-1 ${isToday ? "text-[#00F0FF]" : "text-white"}`}>
                    {format(d, "d")}{isToday && <span className="ml-1 text-[8px] uppercase text-[#00F0FF]">today</span>}
                  </div>
                  {dayShifts.map((s) => (
                    <div key={s.id} className="mb-1 px-1 py-0.5 border-l-2 text-[10px] font-mono-rs text-slate-200 truncate"
                      style={{ borderLeftColor: modColor(s.modality), background: modColor(s.modality) + "1A" }}>
                      {s.start_time}
                    </div>
                  ))}
                </button>
              );
            })}
          </div>
          <DayDetail dateStr={selected} shifts={selectedShifts} />
        </div>
      )}

      {/* WEEK */}
      {view === "week" && (
        <div data-testid="tech-week-grid" className="grid grid-cols-1 sm:grid-cols-7 gap-2 rs-fade-up">
          {weekDays.map((d) => {
            const key = dstr(d);
            const dayShifts = byDate[key] || [];
            const isToday = isSameDay(d, today);
            return (
              <div key={key} data-testid={`tech-weekday-${key}`}
                className={`bg-[#0F1219] border p-3 min-h-[120px] ${isToday ? "border-[#00F0FF]/60" : "border-slate-800"}`}>
                <div className={`font-mono-rs text-xs uppercase mb-2 ${isToday ? "text-[#00F0FF]" : "text-slate-400"}`}>
                  {format(d, "EEE")} <span className="text-white">{format(d, "d")}</span>
                </div>
                {dayShifts.length === 0 && <div className="text-[10px] text-slate-600 font-mono-rs">Off</div>}
                {dayShifts.map((s) => (
                  <div key={s.id} className="mb-2 p-2 border-l-2 bg-[#05060A]" style={{ borderLeftColor: modColor(s.modality) }}>
                    <ModalityBadge modality={s.modality} />
                    <div className="font-mono-rs text-xs text-white mt-1">{s.start_time}–{s.end_time}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* LIST */}
      {view === "list" && (
        <div className="rs-fade-up">
          {nextShift && (
            <div className="bg-[#0F1219] border p-6 mb-6" style={{ borderColor: modColor(nextShift.modality) + "66" }} data-testid="next-shift-card">
              <p className="text-xs tracking-[0.25em] uppercase text-slate-400 mb-3 font-mono-rs">Next Shift</p>
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <div className="font-mono-rs text-4xl font-bold text-white">{format(parseISO(nextShift.date), "EEE d")}</div>
                  <div className="text-sm text-slate-500">{format(parseISO(nextShift.date), "MMMM yyyy")}</div>
                </div>
                <div className="h-12 w-px bg-slate-800" />
                <div>
                  <div className="flex items-center gap-2 mb-1"><ModalityBadge modality={nextShift.modality} /></div>
                  <div className="font-mono-rs text-2xl text-white">{nextShift.start_time}–{nextShift.end_time}</div>
                </div>
              </div>
            </div>
          )}
          <h3 className="font-mono-rs text-sm tracking-[0.2em] uppercase text-slate-400 mb-4">All Upcoming</h3>
          <div className="space-y-3" data-testid="tech-shift-list">
            {upcoming.length === 0 && <p className="text-slate-500 text-sm">No upcoming shifts assigned to you.</p>}
            {upcoming.map((s) => (
              <div key={s.id} data-testid={`tech-shift-${s.id}`}
                className="bg-[#0F1219] border border-slate-800 border-l-2 p-4 flex items-center justify-between hover:border-[#D902EE]/40 transition-all duration-300"
                style={{ borderLeftColor: modColor(s.modality) }}>
                <div className="flex items-center gap-4">
                  <div className="text-center min-w-[52px]">
                    <div className="font-mono-rs text-xl text-white leading-none">{format(parseISO(s.date), "d")}</div>
                    <div className="text-[10px] uppercase text-slate-500">{format(parseISO(s.date), "MMM")}</div>
                  </div>
                  <div className="h-10 w-px bg-slate-800" />
                  <div className="flex items-center gap-2">
                    <ModalityBadge modality={s.modality} />
                    <span className="font-mono-rs text-sm text-white">{s.start_time}–{s.end_time}</span>
                  </div>
                </div>
                <CalendarClock className="w-5 h-5 text-slate-600" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DayDetail({ dateStr, shifts }) {
  return (
    <div className="mt-5 bg-[#0F1219] border border-slate-800 p-4" data-testid="tech-day-detail">
      <p className="text-xs tracking-[0.2em] uppercase text-[#00F0FF] font-mono-rs mb-3">
        {format(parseISO(dateStr), "EEEE, MMMM d")}
      </p>
      {shifts.length === 0 ? (
        <p className="text-slate-500 text-sm">No shift — day off.</p>
      ) : (
        <div className="space-y-2">
          {shifts.map((s) => (
            <div key={s.id} className="flex items-center gap-3 border-l-2 pl-3 py-1" style={{ borderLeftColor: modColor(s.modality) }}>
              <ModalityBadge modality={s.modality} />
              <span className="font-mono-rs text-sm text-white">{s.start_time}–{s.end_time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
